// ============================================================
// EsignedTag.tsx — (E-sign fixes B) the "E-signed" label on a document row:
// a COPY of a sealed e-signed PDF or its completion certificate, saved as an
// ordinary case file (the evidence itself is never touched). Read-only — the
// server refuses rename / delete / move (409 "E-signed copies can’t be changed
// or deleted."), so the lists hide those actions; preview / download as usual.
// (D12) Uploaded by "Casevo e-signature" (a system upload — not a person),
// with "Sent for signing by Priya Shah" in the detail line.
// ============================================================
import React from 'react'
import type { EsignedFileInfo } from '../../types/documentSearch.types'

export const ESIGNED_LOCKED = 'E-signed copies can’t be changed or deleted.'
// (D12) Who "uploaded" an e-signed copy.
export const ESIGN_UPLOADER = 'Casevo e-signature'
export const sentForSigningLine = (e: EsignedFileInfo): string | null => (e.sentByName ? `Sent for signing by ${e.sentByName}` : null)

// (D12) The uploader chip for a system upload: a pen tile (no person's avatar) + "Casevo e-signature".
export const SystemUploaderChip: React.FC<{ name?: string; size?: 'pill' | 'xs' }> = ({ name = ESIGN_UPLOADER, size = 'pill' }) => (
  <span className="flex items-center gap-2 text-[13px] text-casevo-forest min-w-0" data-system-uploader="">
    <span aria-hidden="true" className={`${size === 'xs' ? 'w-[18px] h-[18px]' : 'w-6 h-6'} shrink-0 rounded-full grid place-items-center bg-casevo-mint text-casevo-brand dark:text-casevo-light`}>
      <svg width={size === 'xs' ? 10 : 12} height={size === 'xs' ? 10 : 12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" /></svg>
    </span>
    <span className="truncate" title={name}>{name}</span>
  </span>
)

// "E-signed · Statement SS-0042" / "E-signed · Completion certificate · Statement SS-0042".
export const esignedText = (e: EsignedFileInfo): string =>
  ['E-signed', e.kind === 'CERTIFICATE' ? 'Completion certificate' : null, e.statementLabel ? `Statement ${e.statementLabel}` : null].filter(Boolean).join(' · ')

const EsignedTag: React.FC<{ esigned: EsignedFileInfo; className?: string }> = ({ esigned, className = '' }) => (
  <span className={`inline-flex items-center gap-[6px] flex-wrap text-[11.5px] ${className}`} data-esigned={esigned.kind} title={`${esignedText(esigned)} — ${ESIGNED_LOCKED}`}>
    <span className="inline-flex items-center gap-[4px] font-bold px-2 py-[2px] rounded-full bg-casevo-mint text-casevo-brand dark:text-casevo-light">
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" /></svg>
      E-signed
    </span>
    {esigned.kind === 'CERTIFICATE' && <span className="text-casevo-text-soft">· Completion certificate</span>}
    {esigned.statementLabel && <span className="text-casevo-text-soft">· Statement {esigned.statementLabel}</span>}
    {esigned.sentByName && <span className="text-casevo-text-soft" data-sent-by="">· {sentForSigningLine(esigned)}</span>}
  </span>
)

// (Audit batch 3) The proof of a client's answer / later instruction (the
// server's proofFor — "Used as proof for a client instruction"): it can't be
// deleted while the answer uses it (server 409 PROOF_FILE_LOCKED), so the
// lists show this pill + the lock instead of Delete — like an e-signed copy.
export const PROOF_LOCKED = 'Used as proof for a client instruction, so it can’t be deleted.'
export const ProofTag: React.FC<{ label: string; className?: string }> = ({ label, className = '' }) => (
  <span className={`inline-flex items-center gap-[4px] max-w-full text-[11.5px] font-bold px-2 py-[2px] rounded-full bg-casevo-input text-casevo-text-soft border border-casevo-border ${className}`} data-proof="" title={PROOF_LOCKED}>
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></svg>
    {/* A narrow (phone) row has no room for the sentence — the pill says “Proof”; the tooltip says why. */}
    <span className="sm:hidden whitespace-nowrap">Proof</span>
    <span className="hidden sm:inline">{label}</span>
  </span>
)

export default EsignedTag
